import { FC, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useGalleryAdditionalVisuals, useGalleryAssignment, useGalleryVisualSelection, useGalleryVisualType } from 'components/pages/Gallery/_main/contexts'

import { Nullable } from 'models/helpers'
import { StripeError } from '@stripe/stripe-js'
import { UpsellPayment } from 'components/pages/Gallery/common'
import { ZERO_DECIMAL } from 'constants/misc'
import { decimalFromBig } from 'utils/price'
import { identifiableProduct } from 'utils/serialization/productIdentifier'
import { invalidateListVisuals } from 'dataQueries/visual.query'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { useClientGallery } from 'components/pages/Gallery/_main/ClientGallery/_main'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Gallery upsell payment component.
 * @example <GalleryUpsellPayment />
 */
export const GalleryUpsellPayment: FC = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['gallery', 'gallery_staging_flow'])
  const { userPropertyWorkspaceId } = useUserData()
  const { roles } = useAuth0()
  const { product } = useGalleryAssignment()
  const { assignmentId } = useClientGallery()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const {
    selected,
    selectVisualsRequest,
    selectVisualsMutation,
  } = useGalleryVisualSelection()
  const {
    normalizedOriginalType,
    transformTypes,
  } = useGalleryVisualType()
  const {
    currency,
    selectedAdditionalVisualsCount,
    totalForAdditionalVisualsAfterDiscount,
    additionalVisualProductId,
    isAdditionalVisualsPurchaseModalOpen,
    setIsAdditionalVisualsPurchaseModalOpen,
  } = useGalleryAdditionalVisuals()


  /**
   * Sends the selection, identifying both products by their Product2 code. The payment intent
   * already exists at this point, so a product without a code aborts loudly rather than posting a
   * selection the API would reject.
   */
  const handleConfirmSelection = useCallback((paymentIntentId: string | undefined, billingProductCode: Nullable<string>) => {
    const assignmentProduct = identifiableProduct(product?.productCode)
    const billingProduct = identifiableProduct(billingProductCode)

    if (!assignmentProduct || !billingProduct) {
      spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))

      logger.error('Additional visuals selection not submitted - product carries no product code', undefined, {
        context: {
          component: 'GalleryUpsellPayment',
          action: 'handleConfirmSelection',
          metadata: { assignmentId, hasAssignmentProductCode: !!assignmentProduct, hasBillingProductCode: !!billingProduct },
        },
      })

      return
    }

    selectVisualsMutation.mutate({
      assignmentId,
      workspaceId: userPropertyWorkspaceId,
      isClient: roles.isClient,
      filenames: Array.from(selected),
      product: assignmentProduct,
      billingProduct,
      paymentIntentId,
    })
  }, [assignmentId, product?.productCode, roles.isClient, selectVisualsMutation, selected, spawnErrorToast, t, userPropertyWorkspaceId])

  // Relist visuals if stripe payment fails and user leaves purchase modal without finishing the payment
  const handleAfterAdditionalPurchaseModalClose = useCallback((stripeError: StripeError | null) => {
    if (stripeError) {
      invalidateListVisuals(queryClient, assignmentId)
    }

    selectVisualsMutation.reset()
  }, [selectVisualsMutation, assignmentId, queryClient])

  return (
    <UpsellPayment
      isOpen={isAdditionalVisualsPurchaseModalOpen}
      assignmentId={assignmentId}
      productId={additionalVisualProductId}
      productQuantity={selectedAdditionalVisualsCount}
      total={!!currency ? { value: decimalFromBig(totalForAdditionalVisualsAfterDiscount), currency, discountPercentage: ZERO_DECIMAL } : null}
      beforeConfirmAction={handleConfirmSelection}
      beforeConfirmActionState={selectVisualsRequest?.state}
      beforeConfirmActionMutation={selectVisualsMutation}
      onClose={() => setIsAdditionalVisualsPurchaseModalOpen(false)}
      afterClosed={handleAfterAdditionalPurchaseModalClose}
      onSuccess={() => {
        invalidateListVisuals(queryClient, assignmentId)
        spawnSuccessToast(t('visuals_selected_success'))
        setIsAdditionalVisualsPurchaseModalOpen(false)
      }}
    >
      <Trans t={t} i18nKey="purchase_more_visuals_prompt">
        <span></span>
      </Trans>
    </UpsellPayment>
  )
}
